import {
  ClientResponse,
  ReturnData,
  StorefrontApiClient,
  StorefrontOperations,
  createStorefrontApiClient,
} from "@shopify/storefront-api-client";

export function getShopifyStorefrontClient(
  storeUrl: string,
  publicAccessToken: string,
): ShopifyStorefrontClientInterface {
  if (
    process.env.TEST_TYPE === "e2e_test" &&
    process.env.SHOPIFY_TEST_TYPE !== "real-store"
  ) {
    return new MockShopifyStorefrontClient();
  } else {
    return new ShopifyStorefrontClient(storeUrl, publicAccessToken);
  }
}

export abstract class ShopifyStorefrontClientInterface {
  abstract request<T, V extends Record<string, any>>(
    operation: string,
    variables?: V,
    mockResponse?: T,
  ): Promise<ClientResponse<T>>;
}

export class MockShopifyStorefrontClient extends ShopifyStorefrontClientInterface {
  async request<T, V extends Record<string, any>>(
    operation: string,
    variables?: V,
    mockResponse?: T,
  ): Promise<ClientResponse<T>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return { data: mockResponse ?? ({} as T) };
  }
}
export class ShopifyStorefrontClient extends ShopifyStorefrontClientInterface {
  private client: StorefrontApiClient;
  private tries = 3;

  constructor(storeUrl: string, publicAccessToken: string) {
    super();
    this.client = createStorefrontApiClient({
      storeDomain: `https://${storeUrl}`,
      apiVersion: "2026-07",
      publicAccessToken,
      retries: 3,
    });
  }

  async request<T, V extends Record<string, any>>(
    operation: string,
    variables?: V,
  ): Promise<
    ClientResponse<
      T extends undefined ? ReturnData<string, StorefrontOperations> : T
    >
  > {
    return await this.client.request<T>(operation, {
      variables,
      retries: this.tries,
    });
  }
}

export function handleErrors(errors: any) {
  if (!errors) {
    return;
  }
  const graphQLErrors: { message?: string }[] | undefined =
    errors.graphQLErrors;
  if (graphQLErrors) {
    const fatalErrors = graphQLErrors.filter(
      // Inventory field errors (totalInventory, quantityAvailable without
      // the inventory scope) arrive alongside usable data
      (error) =>
        !error.message?.includes("totalInventory") &&
        !error.message?.includes("unauthenticated_read_product_inventory"),
    );
    if (fatalErrors.length) {
      console.error(
        "GraphQL request failed:",
        errors.status,
        errors.message,
        fatalErrors,
      );
      // Dedupe: field-level errors repeat per product/variant path
      const messages = new Set(
        fatalErrors.map((error) => error.message ?? "Unknown error"),
      );
      throw new Error(`GraphQL request failed: ${[...messages].join("; ")}`);
    }
  } else {
    console.error("GraphQL request failed:", errors.status, errors.message);
    throw new Error("GraphQL request failed. See console for details.");
  }
}
